import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Monitor, LogOut, ShieldCheck } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString();
}

function ActiveSessions() {
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/sessions").then(({ data }) => setSessions(data)).catch(() => setSessions([]));
  useEffect(() => { load(); }, []);

  const revokeOne = async (id) => {
    setBusy(true);
    try { await api.delete(`/auth/sessions/${id}`); toast.success("Signed out that device"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revokeOthers = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/sessions/revoke-others"); toast.success(`Signed out ${data.revoked} other session${data.revoked === 1 ? "" : "s"}`); await load(); }
    catch (e) { toast.error("Could not sign out other sessions"); }
    finally { setBusy(false); }
  };

  const others = (sessions || []).filter((s) => !s.current).length;

  return (
    <Card className="border-zinc-200 shadow-sm" data-testid="active-sessions-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-heading text-xl flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-accent" strokeWidth={1.5} /> Active Sessions</CardTitle>
        {others > 0 && (
          <Button variant="outline" size="sm" onClick={revokeOthers} disabled={busy} data-testid="logout-everywhere-button">
            <LogOut className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Log out other sessions
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {sessions === null ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active sessions.</p>
        ) : (
          <ul className="divide-y divide-zinc-100" data-testid="sessions-list">
            {sessions.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-3" data-testid={`session-row-${s.id}`}>
                <div className="flex items-start gap-3">
                  <Monitor className="h-5 w-5 text-muted-foreground mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-sm font-medium flex items-center gap-2">
                      {s.device}
                      {s.current && <span className="text-[10px] font-semibold uppercase tracking-wider bg-green-100 text-green-800 px-1.5 py-0.5 rounded" data-testid="current-session-badge">This device</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{s.ip || "Unknown IP"} · active {timeAgo(s.last_seen_at)} · {s.kind === "google" ? "Google" : "Password"}</p>
                  </div>
                </div>
                {!s.current && (
                  <Button variant="ghost" size="sm" onClick={() => revokeOne(s.id)} disabled={busy} data-testid={`revoke-session-${s.id}`} className="text-destructive hover:text-destructive">
                    Sign out
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

const MODES = [
  { value: "conservative", label: "Conservative" },
  { value: "balanced", label: "Balanced" },
  { value: "aggressive", label: "Aggressive" },
];
const APP_MODES = [
  { value: "assisted", label: "Assisted", desc: "Review before submitting." },
  { value: "automated", label: "Automated", desc: "Auto-submit when supported (future)." },
];
const LANGS = [
  { value: "en", label: "English" },
  { value: "fr", label: "Français" },
  { value: "es", label: "Español" },
  { value: "de", label: "Deutsch" },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        default_mode: s.default_mode,
        auto_submit: s.default_mode === "automated",
        review_before_submit: s.default_mode === "assisted",
        tailoring_mode: s.tailoring_mode,
        generate_cover_letter: s.generate_cover_letter,
        preferred_language: s.preferred_language,
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-8 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1 text-sm">Defaults applied to new tailoring actions.</p>
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader><CardTitle className="font-heading text-xl">Application Mode</CardTitle></CardHeader>
        <CardContent>
          <RadioGroup value={s.default_mode} onValueChange={(v) => set("default_mode", v)} className="space-y-2">
            {APP_MODES.map((m) => (
              <label key={m.value} htmlFor={`appmode-${m.value}`} data-testid={`appmode-${m.value}`}
                className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${s.default_mode === m.value ? "border-primary bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
                <RadioGroupItem value={m.value} id={`appmode-${m.value}`} className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">{m.label}</p>
                  <p className="text-xs text-muted-foreground">{m.desc}</p>
                </div>
              </label>
            ))}
          </RadioGroup>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader><CardTitle className="font-heading text-xl">Tailoring Defaults</CardTitle></CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider">Default Tailoring Mode</Label>
            <RadioGroup value={s.tailoring_mode} onValueChange={(v) => set("tailoring_mode", v)} className="flex flex-wrap gap-2">
              {MODES.map((m) => (
                <label key={m.value} htmlFor={`tmode-${m.value}`} data-testid={`tmode-${m.value}`}
                  className={`flex items-center gap-2 rounded-md border px-4 py-2 cursor-pointer transition-colors ${s.tailoring_mode === m.value ? "border-primary bg-zinc-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
                  <RadioGroupItem value={m.value} id={`tmode-${m.value}`} />
                  <span className="text-sm font-medium">{m.label}</span>
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="flex items-center justify-between rounded-md border border-zinc-200 p-4">
            <div>
              <p className="text-sm font-medium">Generate cover letter by default</p>
              <p className="text-xs text-muted-foreground">Pre-enable the cover letter option.</p>
            </div>
            <Switch checked={s.generate_cover_letter} onCheckedChange={(v) => set("generate_cover_letter", v)} data-testid="settings-cover-switch" />
          </div>

          <div className="space-y-2 max-w-xs">
            <Label className="text-xs font-semibold uppercase tracking-wider">Preferred Language</Label>
            <Select value={s.preferred_language} onValueChange={(v) => set("preferred_language", v)}>
              <SelectTrigger data-testid="settings-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="active:scale-[0.98] transition-transform">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Save Settings</>}
      </Button>

      <ActiveSessions />
    </div>
  );
}
